if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', class StickyHeader extends HTMLElement {
    constructor() {
      super();
      // 滚动状态
      this.prevY = window.scrollY || 0;
      this.latestY = this.prevY;
      this.rafPending = false;

      // 行为阈值
      this.deltaMin = 5;            // 忽略很小的抖动
      this.revealOffset = 10;        // 向上滚回多少再显示
      this.bgFillOffset = 10;        // 背景填充阈值

      // 状态记忆，避免重复改类
      this.state = { sticky:false, hidden:false, bg:false };
    }

    connectedCallback() {
      // ===== 缓存 DOM =====
      this.header = document.querySelector('.section-header');
      if (!this.header) return;
      this.headerTop = document.querySelector('.header-top');
      this.headerInner = this.header.querySelector('header');
      this.predictiveSearch = this.querySelector('predictive-search');
      this.isToggleNav = this.classList.contains('toggle-nav');
      if (this.isToggleNav) {
        this.header.classList.add('toggle-nav');
        this.toggleNavButton = this.querySelector('.header__toggle-nav-button');
        this.nav = this.querySelector('.header__inline-menu');
        if (this.toggleNavButton) {
          this.onToggleNavClickHandler = this.onToggleNavClick.bind(this);
          this.toggleNavButton.addEventListener('click', this.onToggleNavClickHandler);
        }
      }

      // ===== 尺寸/阈值 =====
      this.updateMetrics(); // 计算 headerBoundTop、CSS 变量

      // Resize 监听（优先 ResizeObserver）
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.updateMetrics());
        if (this.headerInner) this.ro.observe(this.headerInner);
        if (this.headerTop) this.ro.observe(this.headerTop);
        if (this.nav) this.ro.observe(this.nav);
      } else {
        this.onWinResize = this.debounce(() => this.updateMetrics(), 300);
        window.addEventListener('resize', this.onWinResize, false);
      }

      // ===== 滚动监听（被动） =====
      this.onScrollHandler = () => {
        // 如果菜单正在显示（hover 状态），跳过滚动处理
        if (this.header && this.header.preventHide) {
          return;
        }
        this.latestY = window.scrollY || document.documentElement.scrollTop || 0;
        if (!this.rafPending) {
          this.rafPending = true;
          requestAnimationFrame(this.onScrollFrame.bind(this));
        }
      };
      window.addEventListener('scroll', this.onScrollHandler, { passive: true });

      // 初始化一次
      this.onScrollHandler();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScrollHandler);
      if (this.onWinResize) window.removeEventListener('resize', this.onWinResize);
      if (this.ro) this.ro.disconnect();
      if (this.toggleNavButton && this.onToggleNavClickHandler) {
        this.toggleNavButton.removeEventListener('click', this.onToggleNavClickHandler);
      }
    }

    // ====== 计算阈值 & CSS 变量 ======
    updateMetrics() {
      const headerH = this.headerInner ? this.headerInner.clientHeight : this.header.clientHeight;
      const headerTopH = this.headerTop ? this.headerTop.clientHeight : 0;
      const stickyHeaderH = headerH + headerTopH;

      document.body.style.setProperty('--header-height', `${headerH}px`);
      if (window.innerWidth >= 990) {
        document.body.style.setProperty('--sticky-header-height-desktop', `${stickyHeaderH}px`);
      } else {
        document.body.style.setProperty('--sticky-header-height-mobile', `${stickyHeaderH}px`);
      }
      if (this.isToggleNav && this.nav) {
        document.body.style.setProperty('--nav-height', `${this.nav.clientHeight}px`);
      }

      // header 底到页面顶的绝对位置（作为进入/退出 sticky 的界线）
      const rect = this.header.getBoundingClientRect();
      const pageY = window.pageYOffset || document.documentElement.scrollTop || 0;
      this.headerBoundTop = rect.bottom + pageY;
      this.threshold = this.headerBoundTop + 100;
    }

    // ====== 每帧滚动处理（rAF） ======
    onScrollFrame() {
      this.rafPending = false;
      const y = this.latestY;
      const dy = Math.abs(y - this.prevY);
      const goingDown = y > this.prevY;

      // 背景填充（轻量操作）
      this.setBg(y > this.bgFillOffset);

      // 搜索打开时，维持当前状态
      if (this.predictiveSearch && this.predictiveSearch.isOpen) {
        this.prevY = y;
        return;
      }

      if (dy > this.deltaMin) {
        if (goingDown && y > this.headerBoundTop) {
          // 向下且超过 header —— 隐藏
          this.hide();
        } else if (!goingDown && y > this.headerBoundTop + this.revealOffset) {
          // 向上回滚一些 —— 显示（粘顶）
          this.reveal();
        } else if (y <= this.headerBoundTop) {
          // 回到顶部区域 —— 重置
          this.reset();
          this.setBg(false);
        }
      }

      this.prevY = y;
    }

    // ====== 状态切换（避免重复改类） ======
    setBg(on) {
      if (on !== this.state.bg) {
        this.state.bg = on;
        this.header.classList.toggle('header--bg-fill', on);
      }
    }
    setSticky(on) {
      if (on !== this.state.sticky) {
        this.state.sticky = on;
        this.header.classList.toggle('shopify-section-header-sticky', on);
        this.header.classList.toggle('animate', on);
        this.header.classList.toggle('header_h_color', on);
      }
    }
    setHidden(on) {
      if (on !== this.state.hidden) {
        this.state.hidden = on;
        this.header.classList.toggle('shopify-section-header-hidden', on);
      }
    }

    // ====== 公开的动作（复用） ======
    hide() {
      // 如果 header 有 preventHide 属性且为 true，说明菜单正在显示，不隐藏
      if (this.header && this.header.preventHide) {
        return;
      }
      this.setHidden(true);
      this.setSticky(false);
      this.header.classList.remove('header_h_color');
      this.closeMenuDisclosure();
      this.closeSearchModal();
      this.closeToggleNav();
    }

    reveal() {
      this.setSticky(true);
      this.setHidden(false);
      if (this.isToggleNav && this.toggleNavButton) {
        this.toggleNavButton.removeAttribute('tabindex');
      }
    }

    reset() {
      this.setHidden(false);
      this.setSticky(false);
      this.header.classList.remove('animate');
      this.closeToggleNav();
    }

    // ====== ToggleNav ======
    onToggleNavClick() {
      if (!this.nav) return;
      document.body.style.setProperty('--nav-height', `${this.nav.clientHeight}px`);
      this.header.classList.toggle('open-nav');
    }
    closeToggleNav() {
      if (this.isToggleNav && this.toggleNavButton) {
        this.toggleNavButton.setAttribute('tabindex', '-1');
        this.header.classList.remove('open-nav');
      }
    }

    // ====== 其它子组件（保持与你原逻辑一致） ======
    closeMenuDisclosure() {
      this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
      this.disclosures.forEach(d => d.close());
    }
    closeSearchModal() {
      this.searchModal = this.searchModal || this.header.querySelector('details-modal');
      if (this.searchModal) this.searchModal.close(false);
    }

    // ====== 小工具 ======
    debounce(fn, wait) {
      let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), wait); };
    }
  });
}
